import { z } from 'zod';

export function isValidIanaTimezone(tz: string): boolean {
  // `Intl.supportedValuesOf('timeZone')` возвращает только canonical-primary
  // зоны, не link-алиасы. Asia/Ho_Chi_Minh — link на Asia/Saigon; Asia/Makassar
  // — link; в БД они валидны, но при проверке через supportedValuesOf форма
  // молча падает (см. /admin/cities edit для Ньячанга/Бали).
  // Конструктор DateTimeFormat кидает RangeError на невалидной зоне и
  // принимает все известные IANA-зоны включая линки.
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const slug = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z][a-z0-9-]*$/, 'lowercase letters, digits and dashes; starts with a letter');

const countryCode = z
  .string()
  .length(2)
  .regex(/^[A-Z]{2}$/, 'ISO-3166-1 alpha-2');
const timezone = z.string().refine(isValidIanaTimezone, 'invalid IANA timezone');
const locale = z.string().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/, 'BCP 47 (e.g. "ru-RU")');
const currency = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/, 'ISO 4217');

const minPartnerRewardUsdCents = z.number().int().min(1).max(100_000);

export const CityCreateInput = z.object({
  slug,
  name: z.string().min(1).max(120),
  countryCode,
  timezone,
  locale,
  currency,
  isActive: z.boolean().default(true),
  googlePlaceId: z.string().max(200).nullable().default(null),
  minPartnerRewardUsdCents: minPartnerRewardUsdCents.nullable().default(null),
});
export type CityCreateInput = z.infer<typeof CityCreateInput>;

// slug is intentionally absent — immutable after create.
export const CityUpdateInput = z
  .object({
    name: z.string().min(1).max(120),
    countryCode,
    timezone,
    locale,
    currency,
    isActive: z.boolean(),
    googlePlaceId: z.string().max(200).nullable(),
    minPartnerRewardUsdCents: minPartnerRewardUsdCents.nullable(),
  })
  .partial()
  .strict();
export type CityUpdateInput = z.infer<typeof CityUpdateInput>;

export const CityDto = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  countryCode: z.string(),
  timezone: z.string(),
  locale: z.string(),
  currency: z.string(),
  isActive: z.boolean(),
  googlePlaceId: z.string().nullable().optional(),
  minPartnerRewardUsdCents: z.number().int().nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type CityDto = z.infer<typeof CityDto>;

export const UpdateUserCityInput = z.object({
  cityId: z.uuid(),
});
export type UpdateUserCityInput = z.infer<typeof UpdateUserCityInput>;
